/**
 * Website Content — lets an admin edit the corporate site's copy.
 *
 * Every block comes from the backend with its own shape: a line of text, a
 * paragraph, a list of items, or a single item with fields. The editor reads
 * that shape and builds the right inputs, so no block needs code here.
 *
 * Note: the form JSX lives in plain variables, never in a component declared
 * inside this one. A nested component is a new type on every render, which
 * remounts the inputs and drops focus after a single character.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import React, { useEffect, useMemo, useState } from 'react';
import {
  ArrowLeft, ChevronRight, ExternalLink, Eye, FileText, Globe, Plus, RefreshCw,
  Save, Search, Trash2, X,
} from 'lucide-react';

import ContentPreview, { PAGE_PATHS } from './ContentPreview';
import { fetchSiteContentBlocks, updateSiteContentBlock } from './hooks/hooks';

const NAV = '#001f3f';
const TEAL = '#0d9488';

const PAGE_ORDER = ['global', 'home', 'about', 'services', 'projects', 'equipment', 'team',
                    'companies', 'contact', 'minet'];

const titleCase = (field) =>
  field.replace(/_/g, ' ').replace(/([a-z])([A-Z])/g, '$1 $2').replace(/^./, (c) => c.toUpperCase());

// A field holding a lot of prose deserves a textarea rather than one line.
const isLongField = (field, value) =>
  ['bio', 'body', 'desc', 'description', 'paragraph', 'note', 'mission', 'vision', 'lede', 'tagline']
    .some((hint) => field.toLowerCase().includes(hint)) || String(value ?? '').length > 120;

const Toast = ({ toast, onClose }) => {
  if (!toast.open) return null;
  const cls = toast.severity === 'success'
    ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
    : 'bg-red-50 text-red-700 border-red-200';
  return (
    <div className="fixed top-4 right-4 z-50">
      <div className={`${cls} border rounded-lg px-4 py-3 shadow-lg flex items-center gap-2 max-w-sm`}>
        <span className="text-sm font-medium">{toast.message}</span>
        <button onClick={onClose} className="ml-auto text-slate-400 hover:text-slate-600">
          <X size={14} />
        </button>
      </div>
    </div>
  );
};

function WebsiteContent() {
  const queryClient = useQueryClient();
  const { data: blocks, error, isLoading, refetch } = useQuery({
    queryKey: ['site-content-blocks'],
    queryFn: fetchSiteContentBlocks,
  });

  const [editingKey, setEditingKey] = useState(null);
  const [draft, setDraft] = useState(null);
  const [toast, setToast] = useState({ open: false, message: '', severity: '' });
  const [activePage, setActivePage] = useState(null);
  const [query, setQuery] = useState('');
  const [showPreview, setShowPreview] = useState(true);

  const editing = useMemo(
    () => (blocks || []).find((b) => b.key === editingKey) || null,
    [blocks, editingKey],
  );

  // Load the block's stored value into the draft when one is opened.
  useEffect(() => {
    if (!editing) { setDraft(null); return; }
    setDraft(JSON.parse(JSON.stringify(editing.value)));
  }, [editingKey, editing?.updated_at]); // eslint-disable-line react-hooks/exhaustive-deps

  const saveMutation = useMutation({
    mutationFn: updateSiteContentBlock,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['site-content-blocks'] });
      setToast({ open: true, message: 'Saved. The website shows this on the next page load.', severity: 'success' });
      setEditingKey(null);
    },
    onError: (err) => setToast({
      open: true,
      message: err?.response?.data?.error || 'Could not save this block. Try again.',
      severity: 'error',
    }),
  });

  const grouped = useMemo(() => {
    const byPage = {};
    for (const block of blocks || []) {
      (byPage[block.page] = byPage[block.page] || []).push(block);
    }
    return PAGE_ORDER
      .filter((page) => byPage[page])
      .map((page) => ({ page, label: byPage[page][0].page_display, blocks: byPage[page] }));
  }, [blocks]);

  // Land on the first page that has content, until a tab is chosen.
  const currentPage = activePage || grouped[0]?.page || null;

  // Search looks at the labels, the notes and the copy itself, across every
  // page — so "Kariakoo" finds the leadership profile without knowing where
  // it lives. Results ignore the tab selection.
  const searching = query.trim().length > 0;
  const results = useMemo(() => {
    if (!searching) return [];
    const needle = query.trim().toLowerCase();
    return (blocks || []).filter((block) => {
      const haystack = [block.label, block.note, block.key, block.page_display,
                        JSON.stringify(block.value ?? '')].join(' ').toLowerCase();
      return haystack.includes(needle);
    });
  }, [blocks, query, searching]);

  const visibleBlocks = searching
    ? results
    : (grouped.find((g) => g.page === currentPage)?.blocks || []);

  const inputCls = 'w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500';
  const labelCls = 'block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2';

  // ── field renderers ────────────────────────────────────────────────────────
  // Plain functions, never components, and recursive: a block can nest a list
  // of objects (a company's modules) or an object (an app's screenshot), and
  // each level gets the inputs its own shape calls for.
  const isPlainObject = (value) => !!value && typeof value === 'object' && !Array.isArray(value);

  const renderObject = (value, onChange, depth) => (
    <div className={depth > 0 ? 'space-y-4 pl-4 border-l-2 border-slate-100' : 'space-y-5'}>
      {Object.entries(value || {}).map(([field, fieldValue]) => (
        <div key={field}>
          <label className={labelCls}>{titleCase(field)}</label>
          {renderValue(field, fieldValue, (next) => onChange({ ...value, [field]: next }), depth + 1)}
        </div>
      ))}
    </div>
  );

  const renderList = (field, value, onChange, depth) => {
    const entries = value || [];
    const blankEntry = () => {
      const template = entries[0];
      if (template === undefined || typeof template === 'string') return '';
      if (typeof template === 'number') return 0;
      if (Array.isArray(template)) return [];
      return Object.fromEntries(
        Object.entries(template).map(([k, v]) => [k, typeof v === 'number' ? 0 : Array.isArray(v) ? [] : isPlainObject(v) ? {} : '']),
      );
    };
    const replace = (index, next) => onChange(entries.map((entry, i) => (i === index ? next : entry)));
    const swap = (index, delta) => {
      const target = index + delta;
      if (target < 0 || target >= entries.length) return;
      const next = [...entries];
      [next[index], next[target]] = [next[target], next[index]];
      onChange(next);
    };

    return (
      <div className="space-y-3">
        {entries.map((entry, index) => (
          <div key={index} className="border border-slate-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs font-bold text-slate-400">#{index + 1}</span>
              <div className="flex items-center gap-1">
                <button type="button" onClick={() => swap(index, -1)} disabled={index === 0}
                        className="px-2 py-1 text-xs text-slate-500 hover:text-slate-800 disabled:opacity-30"
                        title="Move up">↑</button>
                <button type="button" onClick={() => swap(index, 1)} disabled={index === entries.length - 1}
                        className="px-2 py-1 text-xs text-slate-500 hover:text-slate-800 disabled:opacity-30"
                        title="Move down">↓</button>
                <button type="button" onClick={() => onChange(entries.filter((_, i) => i !== index))}
                        className="px-2 py-1 text-slate-400 hover:text-red-600" title="Remove">
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
            {renderValue(field, entry, (next) => replace(index, next), depth + 1)}
          </div>
        ))}

        <button type="button" onClick={() => onChange([...entries, blankEntry()])}
                className="flex items-center gap-2 px-3 py-2 text-xs font-semibold text-teal-700 border border-teal-200 rounded-lg hover:bg-teal-50">
          <Plus size={13} /> Add item
        </button>
      </div>
    );
  };

  const renderValue = (field, value, onChange, depth = 0) => {
    if (Array.isArray(value)) return renderList(field, value, onChange, depth);
    if (isPlainObject(value)) return renderObject(value, onChange, depth);

    if (typeof value === 'number') {
      return (
        <input type="number" className={inputCls} value={value}
               onChange={(e) => onChange(e.target.value === '' ? '' : Number(e.target.value))} />
      );
    }

    if (typeof value === 'boolean') {
      return (
        <label className="flex items-center gap-2 text-sm text-slate-600">
          <input type="checkbox" checked={value} onChange={(e) => onChange(e.target.checked)} />
          {value ? 'Yes' : 'No'}
        </label>
      );
    }

    if (isLongField(field, value)) {
      return (
        <textarea rows={Math.min(12, Math.max(3, Math.ceil(String(value ?? '').length / 90)))}
                  className={inputCls} value={value ?? ''} onChange={(e) => onChange(e.target.value)} />
      );
    }

    return <input className={inputCls} value={value ?? ''} onChange={(e) => onChange(e.target.value)} />;
  };

  const textEditor = (
    <textarea
      rows={editing?.kind === 'richtext' ? 8 : 3}
      className={inputCls}
      value={draft ?? ''}
      onChange={(e) => setDraft(e.target.value)}
    />
  );

  const editorBody = () => {
    if (draft === null || draft === undefined) return null;
    if (editing.kind === 'list') return renderList(editing.key, draft, setDraft, 0);
    if (editing.kind === 'object') return renderObject(draft, setDraft, 0);
    return textEditor;
  };

  const form = editing && (
    <div className={showPreview ? 'max-w-7xl mx-auto' : 'max-w-3xl mx-auto'}>
      <div className="flex items-center justify-between gap-4 mb-4">
        <button onClick={() => setEditingKey(null)}
                className="flex items-center gap-2 text-sm text-slate-600 hover:text-slate-800 transition-colors">
          <ArrowLeft size={16} />
          <span className="font-medium">Back to Website Content</span>
        </button>

        <button
          onClick={() => setShowPreview((on) => !on)}
          className={`flex items-center gap-2 px-3 py-2 text-xs font-semibold rounded-lg border transition-colors ${
            showPreview
              ? 'text-teal-700 border-teal-200 bg-teal-50'
              : 'text-slate-600 border-slate-200 hover:bg-slate-50'
          }`}
        >
          <Eye size={13} /> {showPreview ? 'Hide preview' : 'Show preview'}
        </button>
      </div>

      <div className={showPreview ? 'grid grid-cols-1 lg:grid-cols-2 gap-5 items-start' : ''}>
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="px-6 py-5 border-b border-slate-100 flex items-center gap-3">
            <FileText size={16} style={{ color: NAV }} />
            <div className="min-w-0">
              <h3 className="text-sm font-bold text-slate-800">{editing.label}</h3>
              <p className="text-xs text-slate-400">
                {editing.page_display} page · {editing.note || 'Shown on the corporate website.'}
              </p>
            </div>
          </div>

          <div className="p-6 space-y-5">
            {editorBody()}

            <div className="flex items-center gap-3 pt-2">
              <button
                onClick={() => saveMutation.mutate({ key: editing.key, value: draft })}
                disabled={saveMutation.isLoading}
                className="flex items-center gap-2 bg-teal-600 text-white py-2.5 px-6 rounded-lg font-semibold text-sm hover:bg-teal-700 transition-colors disabled:opacity-50"
              >
                <Save size={14} />
                {saveMutation.isLoading ? 'Saving…' : 'Save Changes'}
              </button>
              <button
                onClick={() => setDraft(JSON.parse(JSON.stringify(editing.value)))}
                className="text-sm font-medium text-slate-500 hover:text-slate-700"
              >
                Reset
              </button>
            </div>
          </div>
        </div>

        {showPreview && draft !== null && draft !== undefined && (
          <div className="lg:sticky lg:top-4">
            <ContentPreview block={editing} value={draft} />
          </div>
        )}
      </div>
    </div>
  );

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-xl p-6 max-w-md mx-auto">
          <h3 className="text-sm font-bold text-red-800 mb-1">Could not load website content</h3>
          <p className="text-sm text-red-600">Please try again later.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {editing ? form : (
        <div className="max-w-4xl mx-auto space-y-5">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                <Globe size={18} style={{ color: NAV }} /> Website Content
              </h2>
              <p className="text-sm text-slate-500 mt-1">
                The text and lists on the corporate website. Edits appear on the site immediately.
              </p>
            </div>
            <button onClick={refetch}
                    className="flex items-center gap-2 px-3 py-2 text-xs font-medium text-slate-600 border border-slate-200 rounded-lg hover:bg-slate-50 flex-shrink-0">
              <RefreshCw size={13} /> Refresh
            </button>
          </div>

          {/* Search first: it reaches across every page, so nobody has to know
              which page a sentence lives on. */}
          <div className="relative">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search all pages — a heading, a word in the text, anything"
              className="w-full border border-slate-200 rounded-lg pl-9 pr-9 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
            />
            {query ? (
              <button onClick={() => setQuery('')} title="Clear search"
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                <X size={14} />
              </button>
            ) : null}
          </div>

          {/* Page tabs, so switching pages never means scrolling. */}
          {!searching && grouped.length > 0 && (
            <div className="flex items-center gap-1.5 overflow-x-auto pb-1 -mx-1 px-1">
              {grouped.map((group) => {
                const active = group.page === currentPage;
                return (
                  <button
                    key={group.page}
                    onClick={() => setActivePage(group.page)}
                    className={`flex items-center gap-1.5 whitespace-nowrap px-3.5 py-2 rounded-full text-xs font-semibold border transition-colors ${
                      active
                        ? 'text-white border-transparent'
                        : 'text-slate-600 bg-white border-slate-200 hover:bg-slate-50'
                    }`}
                    style={active ? { backgroundColor: NAV } : undefined}
                  >
                    {group.label}
                    <span className={active ? 'text-white/60' : 'text-slate-400'}>
                      {group.blocks.length}
                    </span>
                  </button>
                );
              })}
            </div>
          )}

          {isLoading ? (
            <div className="flex items-center justify-center h-40 text-slate-400">Loading…</div>
          ) : grouped.length === 0 ? (
            <div className="bg-white rounded-xl border border-slate-200 p-10 text-center text-slate-400">
              No content blocks yet.
            </div>
          ) : (
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
              <div className="px-5 py-3 border-b border-slate-100 bg-slate-50 flex items-center justify-between gap-3">
                <p className="text-xs font-bold uppercase tracking-wider" style={{ color: TEAL }}>
                  {searching
                    ? `${visibleBlocks.length} ${visibleBlocks.length === 1 ? 'match' : 'matches'} for "${query.trim()}"`
                    : grouped.find((g) => g.page === currentPage)?.label}
                </p>
                {!searching && PAGE_PATHS[currentPage] ? (
                  <a href={`https://rinterconsultgroup.com${PAGE_PATHS[currentPage]}`}
                     target="_blank" rel="noopener noreferrer"
                     className="flex items-center gap-1.5 text-[11px] font-semibold text-teal-700 hover:text-teal-800 flex-shrink-0">
                    View on site <ExternalLink size={12} />
                  </a>
                ) : null}
              </div>

              {visibleBlocks.length === 0 ? (
                <div className="p-10 text-center text-slate-400 text-sm">
                  Nothing matches that search.
                </div>
              ) : (
                <div className="divide-y divide-slate-100">
                  {visibleBlocks.map((block) => (
                    <button
                      key={block.key}
                      onClick={() => setEditingKey(block.key)}
                      className="w-full flex items-center gap-3 px-5 py-4 text-left hover:bg-slate-50 transition-colors"
                    >
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-semibold text-slate-800 truncate">{block.label}</p>
                        <p className="text-xs text-slate-400 truncate">
                          {searching ? `${block.page_display} · ` : ''}
                          {block.note || (block.kind === 'list'
                            ? `${(block.value || []).length} items`
                            : 'Text')}
                        </p>
                      </div>
                      <ChevronRight size={16} className="text-slate-300 flex-shrink-0" />
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}
      <Toast toast={toast} onClose={() => setToast({ open: false, message: '', severity: '' })} />
    </div>
  );
}

export default WebsiteContent;
